import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type {
  RbacEffectiveAccess,
  RbacRoleDetail,
  RbacRoleInput,
} from '@internal/plugin-rbac-common';

/** The catalogue as the backend reports it, so the UI cannot drift from the policy. */
export interface RbacCatalogue {
  features: {
    id: string;
    title: string;
    path: string;
    read: string;
    actions: { name: string; title: string; description: string }[];
  }[];
}

export interface RbacApi {
  catalogue(): Promise<RbacCatalogue>;
  listRoles(): Promise<RbacRoleDetail[]>;
  createRole(input: RbacRoleInput): Promise<RbacRoleDetail>;
  updateRole(id: string, input: RbacRoleInput): Promise<RbacRoleDetail>;
  deleteRole(id: string): Promise<void>;
  accessFor(entityRef: string): Promise<RbacEffectiveAccess>;
  myAccess(): Promise<RbacEffectiveAccess>;
}

export const rbacApiRef = createApiRef<RbacApi>({
  id: 'plugin.rbac.service',
});

export class RbacClient implements RbacApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  /**
   * RBAC lives inside the permission plugin, so its routes hang off that
   * plugin's base URL rather than one of its own.
   */
  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const base = await this.discoveryApi.getBaseUrl('permission');
    const res = await this.fetchApi.fetch(`${base}${path}`, init);
    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        if (body?.error?.message) detail = body.error.message;
      } catch {
        // Non-JSON body; the status line is all we have.
      }
      throw new Error(detail);
    }
    return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
  }

  private json(method: string, body: unknown): RequestInit {
    return {
      method,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    };
  }

  catalogue() {
    return this.request<RbacCatalogue>('/rbac/catalogue');
  }

  async listRoles() {
    const { roles } = await this.request<{ roles: RbacRoleDetail[] }>(
      '/rbac/roles',
    );
    return roles;
  }

  createRole(input: RbacRoleInput) {
    return this.request<RbacRoleDetail>('/rbac/roles', this.json('POST', input));
  }

  updateRole(id: string, input: RbacRoleInput) {
    return this.request<RbacRoleDetail>(
      `/rbac/roles/${encodeURIComponent(id)}`,
      this.json('PUT', input),
    );
  }

  async deleteRole(id: string) {
    await this.request<void>(`/rbac/roles/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  accessFor(entityRef: string) {
    return this.request<RbacEffectiveAccess>(
      `/rbac/access?entityRef=${encodeURIComponent(entityRef)}`,
    );
  }

  myAccess() {
    return this.request<RbacEffectiveAccess>('/rbac/access/me');
  }
}
